export type StructSchema = {
  schema:
    | Record<string, SchemaValue>
    | Array<{ key: string; value: SchemaValue }>;
};

export type Schema = StructSchema;

export type SchemaValue = {
  structSchema?: StructSchema;
  listSchema?: {
    elementSchema?: SchemaValue;
  };
  intSchema?: Record<string, unknown>;
  doubleSchema?: Record<string, unknown>;
  stringSchema?: Record<string, unknown>;
  boolSchema?: Record<string, unknown>;
};

export const getSchemaType = (
  schemaValue: SchemaValue | null | undefined,
): string | null => {
  if (!schemaValue) {
    return null;
  }

  if (schemaValue.structSchema) {
    return 'struct';
  }
  if (schemaValue.listSchema) {
    return 'list';
  }
  if (schemaValue.intSchema) {
    return 'int';
  }
  if (schemaValue.doubleSchema) {
    return 'double';
  }
  if (schemaValue.stringSchema) {
    return 'string';
  }
  if (schemaValue.boolSchema) {
    return 'bool';
  }

  return null;
};

export type SchemaProperty = {
  name: string;
  type: string;
  depth: number;
  children: SchemaProperty[];
  change?: 'added' | 'removed' | 'unchanged';
};

export const parseSchemaProperties = (
  schema: StructSchema | null | undefined,
  depth: number = 0,
): SchemaProperty[] => {
  const properties: SchemaProperty[] = [];

  if (!schema) {
    return properties;
  }

  const schemaObj = schema.schema;
  const entries = Array.isArray(schemaObj)
    ? schemaObj
    : Object.entries(schemaObj).map(([key, value]) => ({ key, value }));

  for (const entry of entries) {
    const type = getSchemaType(entry.value);

    if (type) {
      const children = [];

      // Recursively parse nested struct schemas
      if (entry.value.structSchema) {
        children.push(
          ...parseSchemaProperties(entry.value.structSchema, depth + 1),
        );
      }

      properties.push({
        name: entry.key,
        type,
        depth,
        children,
      });
    }
  }

  return properties;
};

export const diffSchemas = (
  oldProperties: SchemaProperty[],
  newProperties: SchemaProperty[],
): SchemaProperty[] => {
  // Create a map of old properties by name for easy lookup
  const oldMap = new Map(oldProperties.map(p => [p.name, p]));

  // Collect all unique property names, preserving order from new schema first, then old
  const seenNames = new Set<string>();
  const result: SchemaProperty[] = [];

  // First, add all properties from new schema in order
  for (const newProp of newProperties) {
    if (!seenNames.has(newProp.name)) {
      seenNames.add(newProp.name);
      const oldProp = oldMap.get(newProp.name);

      if (!oldProp) {
        // Added property - recursively diff children if they exist
        result.push({
          ...newProp,
          change: 'added',
          children: diffSchemas([], newProp.children),
        });
      } else {
        result.push({
          ...newProp,
          change: 'unchanged',
          children: diffSchemas(oldProp.children, newProp.children),
        });
      }
    }
  }

  // Then add removed properties from old schema
  for (const oldProp of oldProperties) {
    if (!seenNames.has(oldProp.name)) {
      seenNames.add(oldProp.name);

      // Removed property - recursively diff children if they exist
      result.push({
        ...oldProp,
        change: 'removed',
        children: diffSchemas(oldProp.children, []),
      });
    }
  }

  return result;
};
